/**
 * As partes de áudio da nota da IA que ainda não foram transcritas.
 *
 * O áudio não vai para o servidor como arquivo (0155): cada parte espera AQUI,
 * no aparelho, até a borda transcrever — e sai assim que a transcrição volta.
 * É o que deixa uma consulta de 40 minutos sobreviver a uma queda de rede ou a
 * uma aba recarregada.
 *
 * IndexedDB, e não `sessionStorage`: áudio é binário e passa do limite de 5 MB
 * em poucos minutos. O custo é que a parte sobrevive a fechar a aba — por isso
 * ela some no envio, no descarte da nota e quando a nota não está mais gravando
 * (`limparPartesDasNotas`). Sem IndexedDB (navegação privada antiga, jsdom),
 * a fila vive na memória da aba.
 */

export interface ParteGuardada {
    notaId: string;
    ordem: number;
    segundos: number;
    audio: Blob;
    guardadaEm: string;
}

const BANCO = 'vec-nota-ia';
const LOJA = 'partes';

const naMemoria = new Map<string, ParteGuardada>();
const chave = (notaId: string, ordem: number) => `${notaId}:${String(ordem).padStart(4, '0')}`;

let aberto: Promise<IDBDatabase | null> | null = null;

function abrir(): Promise<IDBDatabase | null> {
    if (aberto) return aberto;
    aberto = new Promise((resolve) => {
        if (typeof indexedDB === 'undefined') {
            resolve(null);
            return;
        }
        try {
            const pedido = indexedDB.open(BANCO, 1);
            pedido.onupgradeneeded = () => {
                pedido.result.createObjectStore(LOJA);
            };
            pedido.onsuccess = () => resolve(pedido.result);
            /* Recusado (modo privado, cota): a memória da aba segura a gravação. */
            pedido.onerror = () => resolve(null);
            pedido.onblocked = () => resolve(null);
        } catch {
            resolve(null);
        }
    });
    return aberto;
}

function esperar<T>(pedido: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        pedido.onsuccess = () => resolve(pedido.result);
        pedido.onerror = () => reject(pedido.error ?? new Error('O navegador recusou guardar o áudio.'));
    });
}

async function loja(modo: IDBTransactionMode): Promise<IDBObjectStore | null> {
    const banco = await abrir();
    return banco ? banco.transaction(LOJA, modo).objectStore(LOJA) : null;
}

export async function guardarParte(parte: ParteGuardada): Promise<void> {
    const l = await loja('readwrite');
    if (!l) {
        naMemoria.set(chave(parte.notaId, parte.ordem), parte);
        return;
    }
    await esperar(l.put(parte, chave(parte.notaId, parte.ordem)));
}

async function todas(): Promise<ParteGuardada[]> {
    const l = await loja('readonly');
    if (!l) return [...naMemoria.values()];
    return esperar(l.getAll() as IDBRequest<ParteGuardada[]>);
}

/** As partes da nota, na ordem da gravação. */
export async function partesGuardadas(notaId: string): Promise<ParteGuardada[]> {
    return (await todas()).filter((p) => p.notaId === notaId).sort((a, b) => a.ordem - b.ordem);
}

export async function tirarParte(notaId: string, ordem: number): Promise<void> {
    const l = await loja('readwrite');
    if (!l) {
        naMemoria.delete(chave(notaId, ordem));
        return;
    }
    await esperar(l.delete(chave(notaId, ordem)));
}

export async function apagarPartesDaNota(notaId: string): Promise<void> {
    for (const p of await partesGuardadas(notaId)) await tirarParte(notaId, p.ordem);
}

/**
 * Áudio de nota que já não grava (gerada, aprovada, descartada) não tem por que
 * ficar. Só as notas passadas: a fila guarda também as de outras consultas.
 */
export async function limparPartesDasNotas(notasQueNaoGravam: string[]): Promise<void> {
    const alvo = new Set(notasQueNaoGravam);
    for (const p of await todas()) {
        if (alvo.has(p.notaId)) await tirarParte(p.notaId, p.ordem);
    }
}

/**
 * Envia as partes guardadas, na ordem. Para na primeira falha: a seguinte
 * provavelmente falharia pelo mesmo motivo, e a ordem de chegada fica perto da
 * ordem da fala.
 */
export async function enviarPartes(
    notaId: string,
    enviar: (parte: ParteGuardada) => Promise<void>,
): Promise<{ enviadas: number; restantes: number; falha?: string }> {
    const partes = await partesGuardadas(notaId);
    let enviadas = 0;
    for (const parte of partes) {
        try {
            await enviar(parte);
        } catch (e) {
            return { enviadas, restantes: partes.length - enviadas, falha: e instanceof Error ? e.message : 'Não foi possível enviar o áudio.' };
        }
        await tirarParte(notaId, parte.ordem);
        enviadas += 1;
    }
    return { enviadas, restantes: 0 };
}

/** Só para os testes: esquece a conexão aberta e a fila em memória. */
export function reiniciarFilaDeAudio(): void {
    naMemoria.clear();
    aberto = null;
}
